import React, {useEffect, useState} from 'react'
import styled from "styled-components";
import {Badge, Button, Card, Col, Descriptions, message, Row, Spin, Tag} from "antd";
import {useNavigate, useParams} from "react-router-dom";
import {useQueryClient} from "react-query";
import {cyan, red, yellow} from "@ant-design/colors";
import {REPO} from "../../Repository/repo";
import {onError, onSubmit} from "../../Helper/Helper";
import {queryKeys} from "../../Constant/QueryKeys";
import {Uploader} from "../../Components";
import CommentBox from "../../Components/Comment/CommentBox";
import useUser from "../../Hooks/useUser";
import {VacationApprovalDetail} from "../../Model/VacationModel";

const {Item} = Descriptions

const Wrap = styled.div`
  .ant-descriptions-item-label { width: 140px; }
`

const BottomWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
`

const STATUS_COLOR: Record<string, string> = {
    "요청중": yellow[6],
    "승인됨": cyan[5],
    "반려됨": red[3],
    "취소됨": "#999",
}

const ApprovalDetail: React.FC = () => {
    const {id} = useParams<{id: string}>()
    const navigate = useNavigate()
    const {userData} = useUser()
    const queryClient = useQueryClient()
    const [isLoading, setIsLoading] = useState(false)
    const [data, setData] = useState<VacationApprovalDetail | null>(null)

    const invalidateVacation = () => {
        queryClient.invalidateQueries(queryKeys.vacationCalendar)
        queryClient.invalidateQueries(queryKeys.vacationAnnual)
        queryClient.invalidateQueries(queryKeys.vacationAnnualDetail)
        queryClient.invalidateQueries(queryKeys.vacationApproval)
    }

    const fetchMethod = async () => {
        if (!id) return
        try {
            setIsLoading(true)
            const res = await REPO.VacationApproval.Detail(id)
            setData(res.data)
        } catch (e: any) {
            if (e?.response?.status === 400) {
                const code = e.response.data?.code
                if (code === 1404 || code === 1403) {
                    onError("확인", e.response.data.message, () => navigate("/Vacation/Approval"))
                }
            }
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => { fetchMethod() }, [id])

    const approvalAction = async (action: string) => {
        if (!id) return
        // 순차 결재: 승인 시 본인보다 앞선 모든 결재자가 이미 '승인' 인지 확인 (반려는 무관)
        if (action === "승인" && data) {
            const selfIndex = data.persons.findIndex(p => p.employee_seq === userData?.data?.seq && !p.person_action)
            if (selfIndex > 0) {
                const prevAllApproved = data.persons.slice(0, selfIndex).every(p => p.person_action === '승인')
                if (!prevAllApproved) {
                    message.warning("이전 결재자의 승인이 완료되지 않았습니다.")
                    return
                }
            }
        }
        try {
            await REPO.VacationApproval.Action(id, "approval", action)
            invalidateVacation()
            fetchMethod()
        } catch (e: any) {
            message.error(e?.response?.data?.message || "처리에 실패했습니다.")
        }
    }

    const cancelAction = async () => {
        if (!id) return
        await REPO.VacationApproval.Action(id, "cancel", "")
        invalidateVacation()
        fetchMethod()
    }

    const onRemove = async () => {
        if (!id) return
        await REPO.VacationApproval.Remove(id)
        invalidateVacation()
        navigate("/Vacation/Approval")
    }

    if (!data) return <Spin spinning={isLoading}><div style={{minHeight: 200}}/></Spin>

    const vac = data.vacation_approval
    const persons = data.persons
    const isDrafter = vac.employee_seq === userData?.data?.seq
    const isApprover = persons.some(p => p.employee_seq === userData?.data?.seq && !p.person_action)
    // 신청자: 요청중 일 때만 취소 가능 / HR admin: 요청중·승인됨 모두 취소 가능
    const canCancel = data.is_hr_admin
        ? (vac.approval_status === '요청중' || vac.approval_status === '승인됨')
        : (isDrafter && vac.approval_status === '요청중')
    const canEdit = !!data.is_update_permit
    const canRemove = vac.approval_status === '요청중' && (isDrafter || data.is_hr_admin)

    return (
        <Wrap>
            <Spin spinning={isLoading}>
                <Row>
                    <Col>
                        <h1>{vac.seq}번 휴가신청서</h1>
                        <Badge color={STATUS_COLOR[vac.approval_status]} status={"success"} count={vac.approval_status}/>
                    </Col>
                    <Col flex={"auto"}>
                        <Row justify={"end"}>
                            {persons.map((p, i) => (
                                <Col span={4} key={i}>
                                    <Card title={p.person_type} bordered size={"small"}>
                                        <b>{p.employee_name} - {p.person_action ?? "요청중"}</b>
                                        <sub style={{display: "block"}}>{p.action_date}</sub>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </Col>
                </Row>

                <Descriptions column={2} bordered>
                    <Item label={"신청자"} span={1}>{vac.employee_name}</Item>
                    <Item label={"신청일"} span={1}>{vac.request_date?.slice(0, 16)}</Item>
                    <Item label={"휴가종류"} span={1}>
                        {vac.vacation_type}{vac.vacation_period_label ? ` · ${vac.vacation_period_label}` : ""}
                    </Item>
                    <Item label={"기간"} span={1}>
                        {vac.vacation_date_start === vac.vacation_date_end
                            ? vac.vacation_date_start
                            : `${vac.vacation_date_start} ~ ${vac.vacation_date_end}`}
                    </Item>
                    <Item label={"사용일수"} span={1}>{vac.vacation_used_days}일</Item>
                    <Item label={"연차차감"} span={1}>{vac.vacation_affects_annual ? vac.vacation_used_days : 0}일</Item>
                    <Item label={"제목"} span={2}>{vac.request_subject}</Item>
                    <Item label={"사유"} span={2}>
                        <div dangerouslySetInnerHTML={{__html: String(vac.request_reason || "")}}/>
                    </Item>
                    <Item label={"첨부파일"} span={2}>
                        <Uploader value={vac.request_extra_attach_file || []} buttonHidden showRemoveIcon={false}/>
                    </Item>
                    <Item label={"댓글"} span={2}>
                        <CommentBox tableRow={`do_vacation_approval:${vac.seq}`}/>
                    </Item>
                </Descriptions>

                <BottomWrap>
                    <div>
                        {isApprover && (
                            <>
                                <Button type={"primary"} style={{marginRight: 5}} onClick={() => onSubmit("승인", "승인 하시겠습니까?", () => approvalAction("승인"))}>승인</Button>
                                <Button style={{marginRight: 5}} onClick={() => onSubmit("반려", "반려 하시겠습니까?", () => approvalAction("반려"))}>반려</Button>
                            </>
                        )}
                        {canCancel && (
                            <Button danger style={{marginRight: 5}} onClick={() => onSubmit("취소", "휴가를 취소하시겠습니까?", cancelAction)}>휴가취소</Button>
                        )}
                    </div>
                    <div>
                        {canRemove && (
                            <Button style={{marginRight: 5}} onClick={() => onSubmit("삭제", "휴가신청서를 삭제하시겠습니까?", onRemove)}>삭제</Button>
                        )}
                        {canEdit && (
                            <Button style={{marginRight: 5}} onClick={() => navigate(`/Vacation/Request/Update/${vac.seq}`)}>수정</Button>
                        )}
                        <Button onClick={() => navigate("/Vacation/Approval")}>목록</Button>
                    </div>
                </BottomWrap>
            </Spin>
        </Wrap>
    )
}

export default ApprovalDetail
